import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const DEFECT_OPTIONS = [
  { v: "repair", label: "Tamir / Yeniden İşlem" },
  { v: "waste", label: "Fire" },
  { v: "second_quality", label: "İkinci Kalite" },
  { v: "pending", label: "Beklemede" },
];

// Kalite Kontrol — mevcut department_jobs (kalite) + quality backend'ini REUSE eder.
// Birim fiyat / KDV / kur DEĞİŞMEZ. Siparişe uygulama kullanıcı onayıyla ve idempotent.
export default function QualityControlDialog({ open, onOpenChange, order, onSaved }) {
  const [job, setJob] = useState(null);
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState("");
  const [defectDisp, setDefectDisp] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    if (!open || !order?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: j } = await api.post("/departments/jobs/ensure", {
          order_id: order.id,
          department: "kalite",
        });
        if (cancelled) return;
        const { data: prev } = await api.get(`/departments/jobs/${j.id}/quality/preview`);
        if (cancelled) return;
        setJob(j);
        const existing = prev.quality?.lines || [];
        const seeded = existing.length
          ? existing.map((li) => ({ ...li }))
          : (prev.lines || []).map((li) => ({
              ...li, passed_qty: li.produced_qty, defective_qty: 0,
            }));
        setRows(seeded);
        setNote(prev.quality?.note || "");
        setDefectDisp(prev.quality?.defect_disposition || "pending");
        setApplied(!!prev.quality?.applied);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, order?.id]);

  const totals = useMemo(() => {
    const produced = rows.reduce((s, r) => s + Number(r.produced_qty || 0), 0);
    const passed = rows.reduce((s, r) => s + Number(r.passed_qty || 0), 0);
    const defective = rows.reduce((s, r) => s + Number(r.defective_qty || 0), 0);
    return { produced, passed, defective, missing: produced - passed - defective };
  }, [rows]);

  const invalid = rows.some((r) => Number(r.passed_qty || 0) + Number(r.defective_qty || 0) > Number(r.produced_qty || 0));

  const save = async () => {
    if (!job || invalid) return;
    setSaving(true);
    try {
      const payload = {
        lines: rows.map((r) => ({
          line_id: r.line_id || "", variant_id: r.variant_id || "", size: r.size || "",
          color: r.color || "", product_name: r.product_name || "",
          produced_qty: Number(r.produced_qty || 0),
          passed_qty: Number(r.passed_qty || 0),
          defective_qty: Number(r.defective_qty || 0),
        })),
        note,
        defect_disposition: totals.defective > 0 ? defectDisp : null,
      };
      const { data } = await api.post(`/departments/jobs/${job.id}/quality`, payload);
      setJob(data);
      toast.success(`Kalite kaydedildi (Sağlam ${totals.passed} / Defolu ${totals.defective})`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const apply = async () => {
    if (!job) return;
    if (!window.confirm(
      `Nihai sağlam adet (${totals.passed}) sipariş/sevkiyat miktarına uygulanacak.\n` +
      `Birim fiyat ve KDV değişmez. Üretim geçmişi (kesim/kalite) korunur.\nOnaylıyor musunuz?`
    )) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/departments/jobs/${job.id}/quality/apply`, {});
      setApplied(true);
      toast.success(`${data.order_no}: sevk edilebilir adet ${data.prev_quantity} → ${data.quantity}`);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quality-dialog" className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-slate-50 tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            Kalite Kontrol — {order?.order_no}
          </DialogTitle>
          <div className="text-[11px] text-slate-500 font-mono">
            {order?.customer_name} · Kesim/üretim geçmişi değişmez
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-sm border border-slate-800 overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-950 text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                <div className="col-span-3">Ürün</div>
                <div className="col-span-1">Renk</div>
                <div className="col-span-1">Beden</div>
                <div className="col-span-2 text-right">Üretime Giren</div>
                <div className="col-span-2 text-right">Sağlam</div>
                <div className="col-span-2 text-right">Defolu</div>
                <div className="col-span-1 text-right">Eksik</div>
              </div>
              <div className="max-h-64 overflow-y-auto kanban-scroll">
                {rows.map((r, idx) => {
                  const missing = Number(r.produced_qty || 0) - Number(r.passed_qty || 0) - Number(r.defective_qty || 0);
                  const bad = missing < 0;
                  return (
                    <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-800 items-center">
                      <div className="col-span-3 text-xs text-slate-200 line-clamp-2" title={r.product_name}>{r.product_name || "—"}</div>
                      <div className="col-span-1 text-xs text-slate-400">{r.color || "—"}</div>
                      <div className="col-span-1 text-xs font-semibold text-slate-100">{r.size || "—"}</div>
                      <div className="col-span-2 text-right text-slate-300 tabular-nums font-mono text-sm" data-testid={`qc-produced-${idx}`}>{r.produced_qty}</div>
                      <div className="col-span-2">
                        <Input
                          data-testid={`qc-passed-${idx}`}
                          type="number" min="0"
                          value={r.passed_qty}
                          onChange={(e) => {
                            const next = [...rows];
                            next[idx] = { ...next[idx], passed_qty: e.target.value };
                            setRows(next);
                          }}
                          className="bg-slate-950 border-slate-700 rounded-sm text-right font-mono h-8"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          data-testid={`qc-defective-${idx}`}
                          type="number" min="0"
                          value={r.defective_qty}
                          onChange={(e) => {
                            const next = [...rows];
                            next[idx] = { ...next[idx], defective_qty: e.target.value };
                            setRows(next);
                          }}
                          className="bg-slate-950 border-slate-700 rounded-sm text-right font-mono h-8"
                        />
                      </div>
                      <div className={`col-span-1 text-right tabular-nums font-mono text-sm ${bad ? "text-red-400" : missing > 0 ? "text-amber-400" : "text-slate-500"}`} data-testid={`qc-missing-${idx}`}>
                        {missing}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-800 bg-slate-950/60 text-xs font-mono">
                <div className="col-span-5 uppercase tracking-widest text-slate-400">Toplam</div>
                <div className="col-span-2 text-right tabular-nums text-slate-200" data-testid="qc-total-produced">{totals.produced}</div>
                <div className="col-span-2 text-right tabular-nums text-emerald-400" data-testid="qc-total-passed">{totals.passed}</div>
                <div className="col-span-2 text-right tabular-nums text-red-400" data-testid="qc-total-defective">{totals.defective}</div>
                <div className={`col-span-1 text-right tabular-nums ${totals.missing < 0 ? "text-red-400" : "text-slate-500"}`} data-testid="qc-total-missing">{totals.missing}</div>
              </div>
            </div>
            {invalid && (
              <div className="text-[11px] text-red-400 font-mono flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Sağlam + Defolu, üretime giren adedi aşamaz.
              </div>
            )}

            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kalite Notu / Açıklama</label>
              <Textarea
                data-testid="qc-note"
                rows={2}
                placeholder="Örn: 3 adet baskı hatası nedeniyle defolu ayrıldı."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="bg-slate-950 border-slate-700 rounded-sm resize-none text-xs"
              />
            </div>

            {totals.defective > 0 && (
              <div className="space-y-2 rounded-sm border border-red-900/60 bg-red-950/20 p-3">
                <div className="text-[10px] uppercase tracking-widest text-red-400 font-mono flex items-center gap-2">
                  <AlertTriangle className="w-3 h-3" /> Defolu {totals.defective} Adet — Durum Seç
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {DEFECT_OPTIONS.map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`qc-defect-${opt.v}`}
                      onClick={() => setDefectDisp(opt.v)}
                      className={`px-2 py-1.5 rounded-sm text-[11px] uppercase tracking-wider font-semibold transition-colors ${
                        defectDisp === opt.v
                          ? "bg-red-600 text-white"
                          : "bg-slate-900 border border-slate-700 text-slate-300 hover:bg-slate-800"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="text-[10px] text-slate-500 font-mono">
                  Defolu ürünler sağlam stok/sevkiyat miktarına DAHİL EDİLMEZ; kaydı burada ayrı tutulur.
                </div>
              </div>
            )}

            {applied && (
              <div className="text-[11px] text-emerald-400 font-mono flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Nihai sağlam adetler sipariş/sevkiyat miktarına uygulandı.
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">Kapat</Button>
          {!applied && (
            <>
              <Button data-testid="save-quality-btn" disabled={saving || loading || invalid} onClick={save} className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase tracking-wider text-xs font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kalite Kaydet"}
              </Button>
              <Button data-testid="apply-quality-btn" disabled={applying || loading || invalid || !job?.quality?.lines?.length} onClick={apply} className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold">
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : `Siparişe Uygula (Sağlam: ${totals.passed})`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
